import { EnqueueActionsController } from "@dqm/react-enqueue-actions-component";
import { PrinterStatusController } from "@dqm/react-printer-status-component";
import { ValueService, ValueStatus } from "@dqm/value-observer";
import React from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { PrinterStatus } from "../../constants/PrinterStatus";
import ErrorContainer from "./../ErrorContainer";
import { UiStatusService } from "../../services/uiStatus/UiStatusService";
import { useUiStatus } from "../../services/uiStatus/UiStatusContext";
import { UiStatus } from "../../services/uiStatus/types/UiStatus";

export interface Props {
  valueService: ValueService;
}

const FaultDialogWrapper: React.FC<Props> = ({ valueService, children }) => {
  const uiStatusService = useUiStatus() as UiStatusService;

  const timeoutOptions = {
    appear: 500,
    enter: 500,
    exit: 300,
  };
  let nodeError = React.useRef(null);
  let nodeContent = React.useRef(null);

  return (
    <EnqueueActionsController valueService={valueService}>
      {({ status: enqueueStatus }: { status: ValueStatus }) => {
        return (
          <PrinterStatusController valueService={valueService}>
            {({ printerStatus }: { printerStatus: string }) => {
              const resolvedStatusInfo = checkForErrors(
                enqueueStatus,
                printerStatus,
                uiStatusService
              );

              return (
                <TransitionGroup component={null}>
                  <CSSTransition
                    key={resolvedStatusInfo.transitionKey}
                    appear={true}
                    timeout={timeoutOptions}
                    classNames="dqm-tcui-box-anim"
                    nodeRef={
                      resolvedStatusInfo.stateIsFaulty ? nodeError : nodeContent
                    }
                  >
                    {resolvedStatusInfo.stateIsFaulty ? (
                      <div
                        ref={nodeError}
                        className="dqm-cui-error-panel dqm-cui-anim-wrapper"
                        data-testid="FaultDialogWrapper-Error"
                      >
                        <ErrorContainer
                          errorClassName={resolvedStatusInfo.errorIconClassName}
                        />
                      </div>
                    ) : (
                      <div
                        ref={nodeContent}
                        className="dqm-cui-anim-wrapper"
                        data-testid="FaultDialogWrapper-Content"
                      >
                        {children}
                      </div>
                    )}
                  </CSSTransition>
                </TransitionGroup>
              );
            }}
          </PrinterStatusController>
        );
      }}
    </EnqueueActionsController>
  );
};

const checkForErrors = (
  enqueueStatus: ValueStatus,
  printerStatus: string,
  uiStatusService: UiStatusService
) => {
  const canPrint: boolean = checkIfPrinterIsOnlineAndReady(printerStatus);
  const errorIconClassName = checkErrorType(enqueueStatus, canPrint);
  let stateIsFaulty: boolean = enqueueStatus === ValueStatus.Fault || !canPrint;
  let transitionKey = "dqm-error-transition-" + stateIsFaulty;

  updateUiErrorStatus(uiStatusService, enqueueStatus, canPrint);

  return { canPrint, errorIconClassName, stateIsFaulty, transitionKey };
};

const checkIfPrinterIsOnlineAndReady = (printerStatus: string) => {
  if (
    printerStatus === PrinterStatus.IDLE ||
    printerStatus === PrinterStatus.PRINTING
  ) {
    return true;
  }
  return false;
};

const checkErrorType = (status: ValueStatus, canPrint: boolean) => {
  return status === ValueStatus.Fault
    ? "connection-error"
    : !canPrint
    ? "printer-error"
    : "";
};

const updateUiErrorStatus = (
  uiStatusService: UiStatusService,
  enqueueStatus: ValueStatus,
  canPrint: boolean
) => {
  if (enqueueStatus === ValueStatus.Fault) {
    uiStatusService.setStatus(UiStatus.CONNECTION_ERROR);
    return;
  }
  if (!canPrint) {
    uiStatusService.setStatus(UiStatus.PRINTER_ERROR);
    return;
  }

  uiStatusService.setStatus(UiStatus.READY);
};

export default FaultDialogWrapper;
